import React, { useState, useEffect } from 'react';
import { Tooltip, IconButton, Box } from '@mui/material';
import { useThemeColors } from '../../../utils/useThemeColors';

const SmallFloatingNavMenu = ({ onNavigate, showOnlySelected, toggleShowOnlySelected, selectedSectionId, disabledSections = {}, hiddenSections = [], onToggleSize, onDockTop }) => {
  const [position, setPosition] = useState({ x: 27, y: Math.round(window.innerHeight / 2) - 150 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const menuItems = [
    { id: 'Configuration', icon: '🛠️', tooltip: 'Window Configuration' },
    { id: 'SizingAndFrameTop', icon: '📐', tooltip: 'Sizing & Frame - Top Row' },
    { id: 'GrillsTop', icon: '▦', tooltip: 'Grills - Top Row' },
    { id: 'SizingAndFrame', icon: '🪟', tooltip: 'Sizing & Frame - Main Row' },
    { id: 'Grills', icon: '▦', tooltip: 'Grills - Main Row' },
    { id: 'Glass', icon: '🔲', tooltip: 'Glass' },
    { id: 'Finish', icon: '🎨', tooltip: 'Finish' },
    { id: 'AdditionalItems', icon: '➕', tooltip: 'Additional Items' },
    { id: 'Installation', icon: '🔧', tooltip: 'Installation' },
    { id: 'Notes', icon: '📝', tooltip: 'Notes' },
    { id: 'Discounts', icon: '💲', tooltip: 'Quantity & Discounts' },
    { id: 'top', icon: '⬆️', tooltip: 'Go to Top' },
    { id: 'toggleView', icon: showOnlySelected ? '📋' : '📄', tooltip: showOnlySelected ? 'Show All Sections' : 'Show Selected Only', isToggle: true },
  ];

  const tc = useThemeColors();

  const handleMouseDown = (e) => {
    if (e.target.closest('.nav-button')) return;
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragOffset.x,
      y: e.clientY - dragOffset.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  const handleClick = (id) => {
    if (disabledSections[id]) return; // Don't navigate to disabled sections
    if (id === 'top') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (id === 'toggleView') {
      toggleShowOnlySelected();
    } else {
      onNavigate(id);
    }
  };

  return (
    <Box
      onMouseDown={handleMouseDown}
      sx={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        zIndex: 9999,
        backgroundColor: tc.panelBg,
        border: `1px solid ${tc.panelBorder}`,
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        padding: '4px',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        width: 'fit-content',
      }}
    >
      {/* Toggle size arrow */}
      <Tooltip
        title="Expand menu"
        arrow
        placement="top"
        disableInteractive
        slotProps={{
          popper: {
            sx: { zIndex: 10000, pointerEvents: 'none' }
          }
        }}
      >
        <IconButton
          className="nav-button"
          onClick={onToggleSize}
          sx={{
            width: '100%',
            height: 16,
            fontSize: '10px',
            padding: 0,
            borderRadius: '4px',
            backgroundColor: tc.headerBg,
            '&:hover': {
              backgroundColor: tc.headerHover,
            },
          }}
        >
          ▼
        </IconButton>
      </Tooltip>
      {onDockTop && (
        <Tooltip
          title="Dock as top bar"
          arrow
          placement="top"
          disableInteractive
          slotProps={{ popper: { sx: { zIndex: 10000, pointerEvents: 'none' } } }}
        >
          <IconButton
            className="nav-button"
            onClick={onDockTop}
            sx={{
              width: '100%',
              height: 16,
              fontSize: '10px',
              padding: 0,
              borderRadius: '4px',
              backgroundColor: tc.headerBg,
              '&:hover': { backgroundColor: tc.headerHover },
            }}
          >
            ⤓
          </IconButton>
        </Tooltip>
      )}
      {/* Menu items grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '2px',
        }}
      >
        {menuItems.filter(item => !hiddenSections.includes(item.id)).map((item) => (
          <Tooltip
            key={item.id}
            title={item.tooltip}
            arrow
            placement="top"
            disableInteractive
            slotProps={{
              popper: {
                sx: { zIndex: 10000, pointerEvents: 'none' }
              }
            }}
          >
            <IconButton
              className="nav-button"
              onClick={() => handleClick(item.id)}
              disabled={disabledSections[item.id]}
              sx={{
                width: 32,
                height: 32,
                fontSize: '16px',
                padding: 0,
                borderRadius: '4px',
                backgroundColor: disabledSections[item.id]
                  ? tc.buttonBgDisabled
                  : item.isToggle
                    ? (showOnlySelected ? tc.buttonBgToggleOn : tc.buttonBgToggleOff)
                    : (showOnlySelected && selectedSectionId === item.id ? tc.buttonBgSelected : tc.buttonBg),
                opacity: disabledSections[item.id] ? 0.4 : 1,
                cursor: disabledSections[item.id] ? 'not-allowed' : 'pointer',
                '&:hover': {
                  backgroundColor: disabledSections[item.id]
                    ? tc.buttonHoverDisabled
                    : item.isToggle
                      ? (showOnlySelected ? tc.buttonHoverActive : tc.buttonHover)
                      : tc.buttonHover,
                },
              }}
            >
              {item.icon}
            </IconButton>
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
};

export default SmallFloatingNavMenu;
